import isEqual from 'lodash/isEqual';
import isPlainObject from 'lodash/isPlainObject';
import {isEmptyValue} from 'schemaDefinition/functions/isEmptyValue';

/**
 * Compares as isEqual, but treats empty values (null, undefined, '' and [])
 * as equal to each other, also when the property is missing altogether.
 *
 * Needed when comparing data that has been cleaned (see
 * cleanInvisibleSchemaValues, which deletes invisible properties also when they
 * are already empty) against uncleaned data. Plain isEqual would report e.g.
 * productFormDetail: [] => <deleted> as a change, causing unchanged entities to
 * be saved.
 */
export const isEqualIgnoringEmpty = (a: unknown, b: unknown): boolean => {
  if (isEmptyValue(a) || isEmptyValue(b)) {
    return isEmptyValue(a) && isEmptyValue(b);
  }

  if (Array.isArray(a) || Array.isArray(b)) {
    return (
      Array.isArray(a) &&
      Array.isArray(b) &&
      a.length === b.length &&
      a.every((item, index) => isEqualIgnoringEmpty(item, b[index]))
    );
  }

  if (isPlainObject(a) && isPlainObject(b)) {
    const objA = a as Record<string, unknown>;
    const objB = b as Record<string, unknown>;
    const keys = new Set([...Object.keys(objA), ...Object.keys(objB)]);

    return [...keys].every(key => isEqualIgnoringEmpty(objA[key], objB[key]));
  }

  return isEqual(a, b);
};
